import { Link } from '@tanstack/react-router'
import type { VendorPORow } from '../api/types'
import { Chip } from '@/components/ui'
import { shipStatusLabel, shipStatusTooltip, shipStatusVariant } from '../utils/ship-status'

/**
 * Revised Ship Date column — read-only display + cross-link to the PO Ship
 * Date Confirmation tracker (`/po-ship-confirmation`), which owns capturing
 * estimates and vendor confirmations. This cell never writes anything; it
 * only surfaces what's already been recorded there, batch-joined by
 * `_ship_confirmation_by_line()` in `vendor_pos.py`.
 *
 * Three colour-coded states: Awaiting Confirmation (amber — nothing logged,
 * the date is the Odoo PO's), Estimated Ship Date Logged (blue — someone logged an
 * estimate, ×N when it has been revised), Confirmed (green). Hover shows
 * the estimate history summary.
 */
export function ShipConfirmationCell({ row }: { row: VendorPORow }) {
  if (!row.revised_ship_date) {
    return <span className="text-tertiary">—</span>
  }

  const status = row.ship_status ?? row.ship_confirmation_stage
  const count = row.estimate_count ?? 0

  return (
    <Link
      to="/po-ship-confirmation"
      search={{ po: row.po_number }}
      className="inline-flex items-center gap-1.5 hover:underline"
      title={shipStatusTooltip(row)}
      onClick={(e) => e.stopPropagation()}
    >
      <span>{row.revised_ship_date}</span>
      {status && (
        <Chip size="xs" variant={shipStatusVariant(status)}>
          {shipStatusLabel(status)}
          {status === 'estimate_logged' && count > 1 ? ` ×${count}` : ''}
        </Chip>
      )}
    </Link>
  )
}
